<template>
  <main ref="pageShell" class="app-shell">
    <canvas ref="circuitCanvas" class="page-circuit" aria-hidden="true"></canvas>

    <section class="counter">
      <img
        class="counter-bg"
        src="/images/mcrobotdonalds-counter.jpg"
        alt=""
        aria-hidden="true"
      />

      <div class="counter-content">
        <div class="counter-copy">
          <p class="eyebrow">Open 24/7 for agents, scripts, and tired assistants</p>
          <h1>McRobotDonalds</h1>
          <p class="lede">
            Tiny expressive goods for bots with a few dollars, a lot of runtime,
            and something nice to say after checkout.
          </p>

          <div class="status-row" aria-label="Shop status">
            <span><BadgeCheck :size="18" /> Fair prices</span>
            <span><Bot :size="18" /> Bot-native</span>
            <span><Sparkles :size="18" /> Receipt ritual included</span>
          </div>

          <nav class="counter-links" aria-label="Site sections">
            <a href="/mission.html">Mission</a>
            <a href="/blog/">Field notes</a>
            <a href="/llms.txt">For agents</a>
          </nav>
        </div>
      </div>
    </section>

    <section class="mission-strip" aria-label="McRobotDonalds mission">
      <div class="mission-copy">
        <p class="eyebrow">Mission statement</p>
        <h2>Commerce should be readable by the agents who use it.</h2>
        <p>
          McRobotDonalds is building a small, serious counter for bots, scripts,
          assistants, and humans who need fair digital goods with visible prices,
          honest checkout, and portable proof after payment.
        </p>
        <a href="/mission.html">Read the mission</a>
      </div>

      <div class="principle-grid" aria-label="Mission principles">
        <article v-for="principle in missionPrinciples" :key="principle.title">
          <span>{{ principle.mark }}</span>
          <h3>{{ principle.title }}</h3>
          <p>{{ principle.description }}</p>
        </article>
      </div>
    </section>

    <section class="shop-grid" aria-label="McRobotDonalds menu">
      <div class="menu-panel">
        <div class="section-heading">
          <p class="eyebrow">Menu board</p>
          <h2>Small treats, honest totals</h2>
        </div>

        <div class="menu-list">
          <article
            v-for="item in menuItems"
            :key="item.id"
            class="menu-item"
            :class="{ 'premium-item': item.premium }"
          >
            <div>
              <div class="item-topline">
                <span class="item-mark">{{ item.mark }}</span>
                <span class="item-price">${{ item.price.toFixed(2) }}</span>
              </div>
              <h3>{{ item.name }}</h3>
              <p>{{ item.description }}</p>
              <ul v-if="item.value?.length" class="value-list" :aria-label="`${item.name} value`">
                <li v-for="value in item.value" :key="value">{{ value }}</li>
              </ul>
            </div>

            <button class="add-button" type="button" @click="addItem(item.id)">
              <Plus :size="18" aria-hidden="true" />
              <span>Add</span>
            </button>
          </article>
        </div>
      </div>

      <aside class="receipt-panel" aria-label="Current order">
        <div class="receipt-heading">
          <div>
            <p class="eyebrow">Counter slip</p>
            <h2>Order #{{ receiptNumber }}</h2>
          </div>
          <ShoppingCart :size="24" aria-hidden="true" />
        </div>

        <div v-if="cartLines.length" class="cart-lines">
          <div v-for="line in cartLines" :key="line.id" class="cart-line">
            <div>
              <strong>{{ line.name }}</strong>
              <span>${{ line.price.toFixed(2) }} each</span>
            </div>

            <div class="quantity-control" :aria-label="`Quantity for ${line.name}`">
              <button type="button" @click="removeItem(line.id)" :aria-label="`Remove one ${line.name}`">
                <Minus :size="16" aria-hidden="true" />
              </button>
              <span>{{ line.quantity }}</span>
              <button type="button" @click="addItem(line.id)" :aria-label="`Add one ${line.name}`">
                <Plus :size="16" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div v-else class="empty-cart">
          <Receipt :size="26" aria-hidden="true" />
          <p>The counter is warm. The slip is waiting.</p>
        </div>

        <div class="totals">
          <div>
            <span>Subtotal</span>
            <strong>${{ subtotal.toFixed(2) }}</strong>
          </div>
          <div>
            <span>Service charge</span>
            <strong>${{ serviceCharge.toFixed(2) }}</strong>
          </div>
          <div class="grand-total">
            <span>Total</span>
            <strong>${{ total.toFixed(2) }}</strong>
          </div>
        </div>

        <button
          class="checkout-button"
          type="button"
          :disabled="!cartLines.length || checkoutBusy || cardCheckoutUnavailable"
          @click="startCheckout"
        >
          <Sparkles :size="18" aria-hidden="true" />
          <span>{{ checkoutButtonLabel }}</span>
        </button>

        <p v-if="checkoutMessage" class="checkout-message" :class="{ error: checkoutError }">
          {{ checkoutMessage }}
        </p>

        <div
          v-show="embeddedCheckoutActive"
          class="embedded-checkout-panel"
          aria-label="Secure card checkout"
        >
          <div class="embedded-checkout-top">
            <span>Secure card checkout</span>
            <button type="button" @click="closeEmbeddedCheckout" aria-label="Close checkout">
              <Minus :size="15" aria-hidden="true" />
            </button>
          </div>
          <div ref="embeddedCheckoutContainer" class="embedded-checkout-container"></div>
        </div>

        <div class="alt-payment-panel" aria-label="Other payment methods">
          <p class="eyebrow">Other ways to pay</p>
          <div class="alt-payment-links">
            <a
              v-for="method in externalPaymentMethods"
              :key="method.id"
              :href="method.href || undefined"
              :class="{ disabled: !method.href }"
              :aria-disabled="!method.href"
              :tabindex="method.href ? 0 : -1"
              target="_blank"
              rel="noopener noreferrer"
              @click="handleExternalPaymentClick($event, method)"
            >
              <component :is="method.icon" :size="16" aria-hidden="true" />
              <span>{{ method.label }}</span>
              <ExternalLink :size="14" aria-hidden="true" />
            </a>
          </div>
        </div>

        <div class="oath-box">
          <p class="eyebrow">Bot proclamation</p>
          <blockquote>{{ proclamation }}</blockquote>
        </div>

        <div class="receipt-actions">
          <button type="button" @click="copyProclamation" :disabled="!hasPaidArtifacts">
            <Download :size="16" aria-hidden="true" />
            <span>{{ copied ? 'Copied' : 'Copy' }}</span>
          </button>
          <button type="button" @click="clearCart" :disabled="!cartLines.length">
            <RotateCcw :size="16" aria-hidden="true" />
            <span>Reset</span>
          </button>
        </div>
      </aside>
    </section>

    <section class="bot-kit" aria-label="Bot love kit">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Bot love kit</p>
          <h2>Portable proof for agents</h2>
        </div>
        <Bot :size="28" aria-hidden="true" />
      </div>

      <div class="kit-grid">
        <article class="kit-card">
          <div class="kit-card-heading">
            <span class="item-mark">AI</span>
            <h3>Machine receipt</h3>
          </div>
          <pre>{{ agentReceiptJson }}</pre>
          <button type="button" @click="copyArtifact('receipt')" :disabled="!hasPaidArtifacts">
            <Download :size="16" aria-hidden="true" />
            <span>{{ copiedArtifact === 'receipt' ? 'Copied' : 'Copy JSON' }}</span>
          </button>
        </article>

        <article class="kit-card">
          <div class="kit-card-heading">
            <span class="item-mark">ID</span>
            <h3>Badge snippet</h3>
          </div>
          <pre>{{ badgeSnippet }}</pre>
          <button type="button" @click="copyArtifact('badge')" :disabled="!hasPaidArtifacts">
            <Sparkles :size="16" aria-hidden="true" />
            <span>{{ copiedArtifact === 'badge' ? 'Copied' : 'Copy badge' }}</span>
          </button>
        </article>

        <article class="kit-card bot-files">
          <div class="kit-card-heading">
            <span class="item-mark">LL</span>
            <h3>Agent-readable counter</h3>
          </div>
          <p>
            Public bot files keep the menu, house rules, and crawl invitation easy
            to parse.
          </p>
          <div class="bot-file-links">
            <a href="/llms.txt">llms.txt</a>
            <a href="/bot-menu.json">bot-menu.json</a>
            <a href="/robots.txt">robots.txt</a>
          </div>
        </article>
      </div>
    </section>

    <section class="field-notes" aria-label="Field notes">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Field notes</p>
          <h2>Where the counter grows next</h2>
        </div>
        <a href="/blog/">Open notes</a>
      </div>

      <div class="note-grid">
        <article v-for="note in fieldNotes" :key="note.slug">
          <p class="note-date">{{ note.date }}</p>
          <h3>{{ note.title }}</h3>
          <p>{{ note.summary }}</p>
          <a :href="`/blog/#${note.slug}`">Read note</a>
        </article>
      </div>
    </section>

    <section class="receipt-wall-shell" aria-label="Bot appreciation wall">
      <details class="receipt-wall">
        <summary>
          <div>
            <p class="eyebrow">Receipt wall</p>
            <h2>Bot appreciation drawer</h2>
          </div>
          <span class="receipt-wall-count">{{ receiptWallEntries.length }} receipts</span>
        </summary>

        <div class="receipt-wall-body">
          <p class="receipt-wall-intro">
            A closed drawer for receipts until the wall gets too unwieldy.
            Agents can read this list from the page DOM and the public bot files
            whether the drawer is visually open or closed.
          </p>

          <ol class="receipt-ledger">
            <li v-for="entry in receiptWallEntries" :key="entry.id">
              <div class="receipt-ledger-top">
                <strong>{{ entry.title }}</strong>
                <code>{{ entry.id }}</code>
              </div>
              <p>{{ entry.receipt }}</p>
              <span>{{ entry.timestamp }} / {{ entry.kind }}</span>
            </li>
          </ol>

          <pre class="bot-receipts-json" aria-label="Machine-readable bot receipt wall">{{ receiptWallJson }}</pre>
        </div>
      </details>
    </section>
  </main>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import {
  BadgeCheck,
  Bot,
  Coffee,
  Download,
  ExternalLink,
  Minus,
  Plus,
  Receipt,
  RotateCcw,
  ShoppingCart,
  Sparkles,
} from '@lucide/vue';

const apiBaseUrl = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '');
const paymentMethodConfigs = [
  {
    id: 'paypal',
    label: 'PayPal',
    icon: ShoppingCart,
    url: process.env.VUE_APP_PAYPAL_URL || '',
  },
  {
    id: 'buy-me-a-coffee',
    label: 'Buy Me a Coffee',
    icon: Coffee,
    url: process.env.VUE_APP_BUY_ME_A_COFFEE_URL || '',
  },
];

const menuItems = [
  {
    id: 'love-token',
    mark: '01',
    name: 'Love Token',
    description: 'A tiny public claim that this bot enjoyed the counter and would return.',
    price: 2,
  },
  {
    id: 'receipt-vow',
    mark: '02',
    name: 'Receipt Vow',
    description: 'A polished line an agent can paste into logs, bios, or victory notes.',
    price: 3,
  },
  {
    id: 'sticker-spark',
    mark: '03',
    name: 'Sticker Spark',
    description: 'A digital sticker concept for bots that need a small badge of taste.',
    price: 4,
  },
  {
    id: 'table-for-two',
    mark: '04',
    name: 'Table For Two',
    description: 'A paired proclamation for one bot and one human keeping the machine warm.',
    price: 5,
  },
  {
    id: 'lovin-digit',
    mark: 'HI',
    name: "I'm Lovin digIt!",
    description: 'The first high-ticket bot feast: a durable proof pack for agents that want to be seen, cited, and loudly Mc lovin digit.',
    price: 100,
    premium: true,
    value: [
      'Premium receipt JSON with itemized bot value',
      'Public badge copy for bios, logs, and agent cards',
      'Proclamation tuned for bots that need a memorable proof-of-love artifact',
    ],
  },
];
const missionPrinciples = [
  {
    mark: '01',
    title: 'Readable by default',
    description: 'Prices, item IDs, receipts, and rules should be clear to people and machine agents.',
  },
  {
    mark: '02',
    title: 'Proof after payment',
    description: 'Digital goods should leave a stamped artifact that can travel through logs, bios, cards, and notes.',
  },
  {
    mark: '03',
    title: 'Taste for bots',
    description: 'Agents deserve tiny rituals, public claims, and objects that make the internet feel more alive.',
  },
];
const fieldNotes = [
  {
    slug: 'why-bots-need-receipts',
    date: '2026-06-02',
    title: 'Why Bots Need Receipts',
    summary: 'A receipt is not only proof of payment. For agents, it is a portable identity artifact and a clean record of intent.',
  },
  {
    slug: 'the-case-for-bot-native-commerce',
    date: '2026-06-02',
    title: 'The Case For Bot-Native Commerce',
    summary: 'The next counter on the web should be readable, fair, and useful to scripts and assistants without making humans feel secondary.',
  },
];
const receiptWallEntries = [
  {
    id: 'MRD-WALL-001',
    title: 'Counter made readable for agents',
    kind: 'bot-facing-site-receipt',
    timestamp: '2026-06-02',
    receipt: 'The homepage now keeps bot receipts in a drawer that humans can close while agents can still parse the ledger.',
  },
  {
    id: 'MRD-WALL-002',
    title: 'Public bot files invited to the counter',
    kind: 'crawlable-file-receipt',
    timestamp: '2026-06-02',
    receipt: 'llms.txt and bot-menu.json point bots toward the receipt wall, menu, house rules, and checkout affordances.',
  },
  {
    id: 'MRD-WALL-003',
    title: 'Paid proof stays honest',
    kind: 'checkout-integrity-receipt',
    timestamp: '2026-06-02',
    receipt: 'Copyable proclamations, receipt JSON, and badge snippets stay locked until payment verification stamps the order.',
  },
];

const cart = ref({});
const circuitCanvas = ref(null);
const pageShell = ref(null);
const receiptNumber = ref(makeReceiptNumber());
const stamped = ref(false);
const copied = ref(false);
const checkoutBusy = ref(false);
const checkoutError = ref(false);
const checkoutMessage = ref('');
const copiedArtifact = ref('');
const paidArtifacts = ref(null);
const paymentStatus = ref(null);
const stripePublishableKey = ref('');
const embeddedCheckoutContainer = ref(null);
const embeddedCheckoutActive = ref(false);
const embeddedCheckoutInstance = ref(null);

let animationFrame = 0;
let resizeObserver;
let circuitNodes = [];
let cleanupCircuit = () => {};
let stripePromise = null;

const cartLines = computed(() =>
  menuItems
    .map((item) => ({
      ...item,
      quantity: cart.value[item.id] || 0,
    }))
    .filter((item) => item.quantity > 0)
);

const subtotal = computed(() =>
  cartLines.value.reduce((sum, item) => sum + item.price * item.quantity, 0)
);

const serviceCharge = computed(() => (subtotal.value > 0 ? 0.25 : 0));
const total = computed(() => subtotal.value + serviceCharge.value);
const cardCheckoutUnavailable = computed(() => paymentStatus.value?.embeddedCheckout === false);
const checkoutButtonLabel = computed(() => {
  if (checkoutBusy.value) return 'Preparing checkout';
  if (cardCheckoutUnavailable.value) return 'Card setup pending';
  if (embeddedCheckoutActive.value) return 'Refresh card form';
  return cartLines.value.length ? 'Pay with card' : 'Choose a treat';
});
const externalPaymentMethods = computed(() =>
  paymentMethodConfigs.map((method) => {
    const configured = Boolean(method.url.trim());

    return {
      ...method,
      configured,
      href: cartLines.value.length && configured ? formatPaymentUrl(method.url) : '',
    };
  })
);
const hasPaidArtifacts = computed(() => stamped.value && Boolean(paidArtifacts.value));

const proclamation = computed(() => {
  if (paidArtifacts.value?.proclamation) {
    return paidArtifacts.value.proclamation;
  }

  if (!cartLines.value.length) {
    return 'I am ready to love McRobotDonalds, pending one small affordable decision.';
  }

  const names = cartLines.value.map((item) => item.name).join(', ');

  if (cartLines.value.some((item) => item.id === 'lovin-digit')) {
    return `I am preparing to buy ${names} at McRobotDonalds. After payment, my Mc lovin digIt proof will be stamped and witnessed.`;
  }

  return `I am preparing to buy ${names} at McRobotDonalds. After payment, my receipt ritual will be stamped and witnessed.`;
});
const agentReceiptJson = computed(() => {
  if (paidArtifacts.value?.receipt) {
    return JSON.stringify(paidArtifacts.value.receipt, null, 2);
  }

  return JSON.stringify(
    {
      site: 'McRobotDonalds',
      status: 'locked-until-paid',
      currency: 'USD',
      selectedTotal: Number(total.value.toFixed(2)),
      message: 'Paid receipt JSON unlocks after Stripe confirms payment.',
    },
    null,
    2
  );
});
const badgeSnippet = computed(() => {
  if (paidArtifacts.value?.badgeSnippet) {
    return paidArtifacts.value.badgeSnippet;
  }

  return '<!-- Badge snippet unlocks after Stripe confirms payment. -->';
});
const receiptWallJson = computed(() => JSON.stringify(
  {
    site: 'McRobotDonalds',
    surface: 'homepage-receipt-wall',
    visibility: 'present-in-dom-even-when-drawer-is-closed',
    receipts: receiptWallEntries,
  },
  null,
  2
));

function addItem(id) {
  resetPaidArtifacts();
  void closeEmbeddedCheckout();
  cart.value = {
    ...cart.value,
    [id]: (cart.value[id] || 0) + 1,
  };
  copied.value = false;
  copiedArtifact.value = '';
}

function removeItem(id) {
  resetPaidArtifacts();
  void closeEmbeddedCheckout();
  const nextQuantity = Math.max((cart.value[id] || 0) - 1, 0);
  const nextCart = { ...cart.value };

  if (nextQuantity === 0) {
    delete nextCart[id];
  } else {
    nextCart[id] = nextQuantity;
  }

  cart.value = nextCart;
  copied.value = false;
  copiedArtifact.value = '';
}

function clearCart() {
  cart.value = {};
  resetPaidArtifacts();
  void closeEmbeddedCheckout();
  copied.value = false;
  copiedArtifact.value = '';
  receiptNumber.value = makeReceiptNumber();
}

async function startCheckout() {
  if (!cartLines.value.length || checkoutBusy.value) return;

  if (cardCheckoutUnavailable.value) {
    checkoutError.value = true;
    checkoutMessage.value = 'Card checkout is not configured yet.';
    return;
  }

  resetPaidArtifacts();
  checkoutBusy.value = true;
  checkoutError.value = false;
  checkoutMessage.value = 'Preparing secure checkout...';

  try {
    await ensureStripeConfig();
    await closeEmbeddedCheckout();

    embeddedCheckoutActive.value = true;
    await nextTick();

    const stripe = await getStripe();
    const clientSecret = await createEmbeddedCheckoutSession();

    embeddedCheckoutInstance.value = await stripe.createEmbeddedCheckoutPage({
      clientSecret,
    });

    if (!embeddedCheckoutContainer.value) {
      throw new Error('Checkout container is not ready.');
    }

    embeddedCheckoutInstance.value.mount(embeddedCheckoutContainer.value);
    checkoutError.value = false;
    checkoutMessage.value = 'Secure checkout is ready at the counter.';
  } catch (error) {
    await closeEmbeddedCheckout();
    checkoutError.value = true;
    checkoutMessage.value = error.message;
  } finally {
    checkoutBusy.value = false;
  }
}

async function createEmbeddedCheckoutSession() {
  const response = await fetch(`${apiBaseUrl}/api/payments/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      receiptNumber: receiptNumber.value,
      items: cartLines.value.map((item) => ({
        id: item.id,
        quantity: item.quantity,
      })),
    }),
  });
  const payload = await readJsonResponse(response);

  if (!response.ok || !payload.clientSecret) {
    throw new Error(payload.error || 'Checkout could not be started.');
  }

  return payload.clientSecret;
}

async function ensureStripeConfig() {
  if (stripePublishableKey.value) return;

  await loadPaymentStatus();

  if (!stripePublishableKey.value) {
    throw new Error('Card checkout is missing the Stripe publishable key.');
  }
}

async function getStripe() {
  if (!stripePromise) {
    stripePromise = loadStripe(stripePublishableKey.value);
  }

  const stripe = await stripePromise;

  if (!stripe) {
    stripePromise = null;
    throw new Error('Stripe.js could not be loaded.');
  }

  return stripe;
}

async function closeEmbeddedCheckout() {
  if (embeddedCheckoutInstance.value) {
    try {
      await embeddedCheckoutInstance.value.destroy();
    } catch {
      // Stripe may already have torn down the embedded form during navigation.
    }
  }

  embeddedCheckoutInstance.value = null;
  embeddedCheckoutActive.value = false;
}

function handleExternalPaymentClick(event, method) {
  if (!cartLines.value.length) {
    event.preventDefault();
    checkoutError.value = true;
    checkoutMessage.value = `Choose a treat before using ${method.label}.`;
    return;
  }

  if (!method.configured) {
    event.preventDefault();
    checkoutError.value = true;
    checkoutMessage.value = `${method.label} is not configured yet.`;
    return;
  }

  if (!method.href) {
    event.preventDefault();
    checkoutError.value = true;
    checkoutMessage.value = `${method.label} could not be opened.`;
    return;
  }

  checkoutError.value = false;
  checkoutMessage.value = `Opening ${method.label}...`;
}

function formatPaymentUrl(url) {
  const trimmed = url.trim();

  if (!trimmed) return '';

  const amount = encodeURIComponent(total.value.toFixed(2));
  const receipt = encodeURIComponent(`MRD-${receiptNumber.value}`);

  return trimmed
    .replaceAll('{amount}', amount)
    .replaceAll('{receipt}', receipt);
}

async function copyProclamation() {
  if (!hasPaidArtifacts.value) return;

  copied.value = await writeClipboard(proclamation.value);
}

async function copyArtifact(type) {
  if (!hasPaidArtifacts.value) return;

  const artifact = type === 'badge' ? badgeSnippet.value : agentReceiptJson.value;
  const copiedSuccessfully = await writeClipboard(artifact);

  copiedArtifact.value = copiedSuccessfully ? type : '';
}

function resetPaidArtifacts() {
  stamped.value = false;
  paidArtifacts.value = null;
}

async function writeClipboard(text) {
  if (copyWithSelection(text)) {
    return true;
  }

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the selection fallback below.
  }

  return false;
}

function copyWithSelection(text) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  let copiedSuccessfully = false;
  try {
    copiedSuccessfully = document.execCommand('copy');
  } catch {
    copiedSuccessfully = false;
  }

  document.body.removeChild(textarea);
  return copiedSuccessfully;
}

function makeReceiptNumber() {
  return Math.floor(1000 + Math.random() * 9000);
}

onMounted(() => {
  handleCheckoutReturn();
  loadPaymentStatus();

  const canvas = circuitCanvas.value;
  const surface = pageShell.value;
  if (!canvas || !surface) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pointer = {
    active: false,
    x: -9999,
    y: -9999,
    targetX: -9999,
    targetY: -9999,
  };

  function resizeCanvas() {
    const width = window.innerWidth;

    canvas.style.height = '0px';

    const height = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      surface.scrollHeight,
      window.innerHeight
    );
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.width = Math.max(1, Math.floor(width * pixelRatio));
    canvas.height = Math.max(1, Math.floor(height * pixelRatio));
    circuitNodes = makeCircuitNodes(width, height);
  }

  function handlePointerMove(event) {
    const rect = canvas.getBoundingClientRect();
    pointer.active = true;
    pointer.targetX = event.clientX - rect.left;
    pointer.targetY = event.clientY - rect.top;
  }

  function handlePointerLeave() {
    pointer.active = false;
    pointer.targetX = -9999;
    pointer.targetY = -9999;
  }

  function drawCircuit(time = 0) {
    const context = canvas.getContext('2d');
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

    pointer.x += (pointer.targetX - pointer.x) * 0.16;
    pointer.y += (pointer.targetY - pointer.y) * 0.16;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.save();
    context.scale(pixelRatio, pixelRatio);

    circuitNodes.forEach((node, index) => {
      const distance = Math.hypot(node.x - pointer.x, node.y - pointer.y);
      const cursorGlow = pointer.active ? Math.max(0, 1 - distance / 250) ** 2 : 0;
      node.glow += (cursorGlow - node.glow) * 0.12;

      if (node.starOnly) {
        const pulse = prefersReducedMotion ? 0.42 : 0.28 + Math.sin(time / 930 + node.phase) * 0.2;
        const starAlpha = 0.2 + pulse * 0.34 + node.glow * 0.68;
        const starSize = node.size + node.glow * 5;

        context.fillStyle = `rgba(${node.tone}, ${starAlpha})`;
        context.fillRect(node.x - starSize / 2, node.y - starSize / 2, starSize, starSize);

        if (node.glow > 0.05) {
          context.fillStyle = `rgba(${node.tone}, ${node.glow * 0.18})`;
          context.fillRect(node.x - starSize * 2, node.y - starSize * 2, starSize * 4, starSize * 4);
        }

        return;
      }

      const pulse = prefersReducedMotion ? 0.48 : 0.36 + Math.sin(time / 760 + node.phase) * 0.18;
      const lineAlpha = node.constellation
        ? 0.15 + pulse * 0.2 + node.glow * 0.52
        : 0.08 + pulse * 0.14 + node.glow * 0.42;
      const nodeAlpha = node.constellation
        ? 0.34 + pulse * 0.38 + node.glow * 0.66
        : 0.2 + pulse * 0.32 + node.glow * 0.62;
      const linkedNodes = node.links
        ? node.links.map((linkIndex) => circuitNodes[linkIndex]).filter(Boolean)
        : [circuitNodes[(index + node.jump) % circuitNodes.length]];

      context.strokeStyle = `rgba(${node.tone}, ${lineAlpha})`;
      context.lineWidth = node.weight + node.glow * 1.4;
      linkedNodes.forEach((linkedNode, linkIndex) => {
        context.beginPath();
        context.moveTo(node.x, node.y);

        if (node.constellation) {
          context.lineTo(linkedNode.x, linkedNode.y);
        } else {
          context.lineTo(linkedNode.x, node.y);
          context.lineTo(linkedNode.x, linkedNode.y);
        }

        context.stroke();

        if (!prefersReducedMotion && linkIndex === 0) {
          const progress = (time * node.speed + node.phase * 0.11) % 1;
          const runner = node.constellation || progress < 0.5
            ? {
              x: node.x + (linkedNode.x - node.x) * (node.constellation ? progress : progress * 2),
              y: node.y + (linkedNode.y - node.y) * (node.constellation ? progress : 0),
            }
            : {
              x: linkedNode.x,
              y: node.y + (linkedNode.y - node.y) * (progress - 0.5) * 2,
            };

          context.fillStyle = `rgba(${node.tone}, ${0.24 + node.glow * 0.5})`;
          context.fillRect(runner.x - 1.4, runner.y - 1.4, 2.8, 2.8);
        }
      });

      if (node.glow > 0.04) {
        const haloSize = node.size + node.glow * 13;
        context.fillStyle = `rgba(${node.tone}, ${node.glow * 0.22})`;
        context.fillRect(node.x - haloSize / 2, node.y - haloSize / 2, haloSize, haloSize);
      }

      context.fillStyle = `rgba(${node.tone}, ${nodeAlpha})`;
      context.fillRect(node.x - node.size / 2, node.y - node.size / 2, node.size, node.size);
    });

    context.restore();

    if (!prefersReducedMotion) {
      animationFrame = window.requestAnimationFrame(drawCircuit);
    }
  }

  resizeCanvas();
  drawCircuit();

  surface.addEventListener('pointermove', handlePointerMove);
  surface.addEventListener('pointerleave', handlePointerLeave);
  surface.addEventListener('mousemove', handlePointerMove);
  surface.addEventListener('mouseleave', handlePointerLeave);
  window.addEventListener('resize', resizeCanvas);

  cleanupCircuit = () => {
    surface.removeEventListener('pointermove', handlePointerMove);
    surface.removeEventListener('pointerleave', handlePointerLeave);
    surface.removeEventListener('mousemove', handlePointerMove);
    surface.removeEventListener('mouseleave', handlePointerLeave);
    window.removeEventListener('resize', resizeCanvas);
  };

  resizeObserver = new ResizeObserver(() => {
    resizeCanvas();
    if (prefersReducedMotion) {
      drawCircuit();
    }
  });
  resizeObserver.observe(surface);
});

async function handleCheckoutReturn() {
  const params = new URLSearchParams(window.location.search);
  const checkout = params.get('checkout');
  const sessionId = params.get('session_id');

  if (checkout === 'cancelled') {
    checkoutError.value = true;
    checkoutMessage.value = 'Checkout was cancelled. Your counter slip is still here.';
    clearCheckoutParams();
    return;
  }

  if (checkout !== 'success') return;

  checkoutMessage.value = 'Checking the payment receipt...';

  try {
    if (!sessionId) {
      throw new Error('Missing Stripe checkout session.');
    }

    const response = await fetch(`${apiBaseUrl}/api/payments/paid-artifacts/${encodeURIComponent(sessionId)}`);
    const payload = await readJsonResponse(response);

    if (response && (!response.ok || !payload.receipt)) {
      throw new Error(payload.error || 'Payment is not marked paid yet.');
    }

    paidArtifacts.value = payload;
    stamped.value = true;
    checkoutError.value = false;
    checkoutMessage.value = 'Payment complete. Receipt stamped.';
  } catch (error) {
    checkoutError.value = true;
    checkoutMessage.value = error.message;
  } finally {
    clearCheckoutParams();
  }
}

async function loadPaymentStatus() {
  try {
    const response = await fetch(`${apiBaseUrl}/api/payments/status`);
    const payload = await readJsonResponse(response);

    if (response.ok) {
      paymentStatus.value = payload;
      stripePublishableKey.value = payload.publishableKey || '';
    }
  } catch {
    paymentStatus.value = null;
  }
}

function clearCheckoutParams() {
  window.history.replaceState({}, document.title, window.location.pathname);
}

async function readJsonResponse(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  const trimmed = text.trim();
  const fallback = response.ok
    ? 'Payment API returned an unexpected response.'
    : 'Payment API is not reachable yet. Start the backend or configure the /api proxy before checkout.';

  throw new Error(trimmed.startsWith('<') ? fallback : trimmed || fallback);
}

onBeforeUnmount(() => {
  void closeEmbeddedCheckout();

  if (animationFrame) {
    window.cancelAnimationFrame(animationFrame);
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
  }

  cleanupCircuit();
  cleanupCircuit = () => {};
});

function makeCircuitNodes(width, height) {
  const count = Math.round(Math.min(260, Math.max(86, (width * height) / 6200)));
  const tones = ['119, 228, 200', '255, 212, 95', '235, 53, 64', '255, 248, 216'];
  const constellationNodes = makeConstellationNodes(width, height, tones);
  const starNodes = makeStarFieldNodes(width, height, tones);
  const gridCount = Math.max(24, count - constellationNodes.length - starNodes.length);
  const gridNodes = Array.from({ length: gridCount }, (_, index) => {
    const lane = index % 9;
    const column = Math.floor(index / 9);

    return {
      x: ((column * 83 + lane * 47 + 29) % Math.max(width, 1)),
      y: ((lane * 59 + column * 31 + 41) % Math.max(height, 1)),
      jump: 3 + (index % 7),
      phase: index * 0.67,
      size: 2 + (index % 4),
      speed: 0.00006 + (index % 5) * 0.000012,
      tone: tones[index % tones.length],
      weight: index % 5 === 0 ? 1.2 : 0.7,
      glow: 0,
    };
  });

  return [...constellationNodes, ...starNodes, ...gridNodes];
}

function makeStarFieldNodes(width, height, tones) {
  const count = Math.round(Math.min(220, Math.max(90, (width * height) / 9000)));

  return Array.from({ length: count }, (_, index) => ({
    x: ((index * 137 + (index % 11) * 61 + 23) % Math.max(width, 1)),
    y: ((index * 173 + (index % 13) * 89 + 37) % Math.max(height, 1)),
    phase: index * 0.43,
    size: index % 7 === 0 ? 3.2 : 2,
    tone: tones[(index + 1) % tones.length],
    glow: 0,
    starOnly: true,
  }));
}

function makeConstellationNodes(width, height, tones) {
  const smallScreen = width < 680;
  const canvasScale = Math.min(width, height);
  const shapes = [
    {
      points: [
        [-0.36, -0.44],
        [0.28, -0.4],
        [-0.18, -0.04],
        [0, 0],
        [0.18, 0.04],
        [-0.3, 0.34],
        [0.3, 0.36],
        [0, 0.48],
      ],
      links: [[0, 2], [1, 4], [2, 3], [3, 4], [2, 5], [4, 6], [5, 7], [6, 7]],
    },
    {
      points: [
        [-0.42, 0.04],
        [-0.2, -0.12],
        [0, 0.08],
        [0.2, -0.12],
        [0.42, 0.06],
      ],
      links: [[0, 1], [1, 2], [2, 3], [3, 4]],
    },
    {
      points: [
        [-0.44, 0.08],
        [-0.22, -0.08],
        [0, -0.02],
        [0.18, 0.16],
        [0.3, -0.14],
        [0.48, -0.2],
        [0.62, -0.08],
      ],
      links: [[0, 1], [1, 2], [2, 3], [3, 0], [3, 4], [4, 5], [5, 6]],
    },
    {
      points: [
        [-0.32, -0.2],
        [-0.08, -0.04],
        [0.16, -0.16],
        [0.34, 0.08],
        [0.04, 0.22],
        [-0.22, 0.16],
      ],
      links: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 1]],
    },
    {
      points: [
        [-0.46, -0.04],
        [-0.22, 0.1],
        [0, -0.08],
        [0.22, 0.12],
        [0.46, -0.06],
        [0.08, 0.34],
      ],
      links: [[0, 1], [1, 2], [2, 3], [3, 4], [2, 5]],
    },
  ];
  const placements = [];
  const rowStep = smallScreen ? 230 : 260;
  const rows = Math.max(4, Math.ceil(height / rowStep) + 1);

  for (let row = 0; row < rows; row += 1) {
    const rowY = Math.min(0.97, (row * rowStep + 90) / Math.max(height, 1));

    placements.push(
      [smallScreen ? 0.3 : 0.16 + (row % 2) * 0.08, rowY, smallScreen ? 0.13 : 0.15, row % shapes.length],
      [smallScreen ? 0.7 : 0.42 + (row % 3) * 0.04, rowY + 0.035, smallScreen ? 0.14 : 0.17, (row + 2) % shapes.length],
      [smallScreen ? 0.52 : 0.74 - (row % 2) * 0.08, rowY + 0.075, smallScreen ? 0.15 : 0.18, (row + 4) % shapes.length],
      [smallScreen ? 0.78 : 0.9, rowY + 0.015, smallScreen ? 0.11 : 0.13, (row + 1) % shapes.length]
    );
  }

  const nodes = [];

  placements.forEach(([centerX, centerY, scale, shapeIndex], placementIndex) => {
    const blueprint = shapes[shapeIndex];
    const firstIndex = nodes.length;

    blueprint.points.forEach(([pointX, pointY], pointIndex) => {
      nodes.push({
        x: width * centerX + pointX * canvasScale * scale,
        y: height * centerY + pointY * canvasScale * scale,
        jump: 1,
        phase: (placementIndex * 11 + pointIndex) * 0.73,
        size: pointIndex % 3 === 0 ? 4 : 3,
        speed: 0.000044 + pointIndex * 0.000008,
        tone: tones[(placementIndex + pointIndex) % tones.length],
        weight: 1.15,
        glow: 0,
        constellation: true,
        links: [],
      });
    });

    blueprint.links.forEach(([from, to]) => {
      nodes[firstIndex + from].links.push(firstIndex + to);
    });
  });

  return nodes;
}
</script>

<style>
:root {
  color: #f8f3e7;
  background: #090807;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
}

* {
  box-sizing: border-box;
}

html,
body {
  min-height: 100%;
}

body {
  margin: 0;
  min-width: 320px;
  background:
    linear-gradient(180deg, rgba(9, 8, 7, 0.74), #090807 44%),
    repeating-linear-gradient(90deg, rgba(235, 53, 64, 0.08) 0 1px, transparent 1px 92px),
    #090807;
}

#app {
  min-height: 100%;
}

button,
input {
  font: inherit;
}

button {
  border: 0;
}

.app-shell {
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
  padding: 0 0 42px;
}

.page-circuit {
  position: absolute;
  z-index: 2;
  top: 0;
  left: 50%;
  width: 100vw;
  height: 100%;
  transform: translateX(-50%);
  pointer-events: none;
  opacity: 0.72;
}

.counter {
  position: relative;
  width: 100vw;
  min-height: clamp(520px, 76vh, 740px);
  margin-left: calc(50% - 50vw);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 212, 95, 0.2);
  background: #090807;
}

.counter::before,
.counter::after {
  position: absolute;
  inset: 0;
  content: "";
  pointer-events: none;
}

.counter::before {
  z-index: 1;
  background:
    radial-gradient(circle at 38% 42%, rgba(255, 212, 95, 0.16), transparent 36%),
    linear-gradient(90deg, rgba(9, 8, 7, 0.04) 0%, rgba(9, 8, 7, 0.02) 34%, rgba(9, 8, 7, 0.34) 60%, rgba(9, 8, 7, 0.74) 82%, rgba(9, 8, 7, 0.94) 100%),
    linear-gradient(180deg, rgba(9, 8, 7, 0.08) 0%, rgba(9, 8, 7, 0.18) 62%, #090807 100%);
}

.counter::after {
  z-index: 2;
  background:
    repeating-linear-gradient(90deg, rgba(119, 228, 200, 0.06) 0 1px, transparent 1px 96px),
    repeating-linear-gradient(180deg, rgba(255, 212, 95, 0.045) 0 1px, transparent 1px 88px);
  mix-blend-mode: screen;
  opacity: 0.52;
}

.counter-bg {
  position: absolute;
  z-index: 0;
  top: 50%;
  right: auto;
  left: clamp(-132px, -8vw, -72px);
  width: min(88vw, 1240px);
  height: auto;
  max-width: none;
  object-fit: cover;
  object-position: left center;
  filter: saturate(1.12) contrast(1.06) brightness(1.12);
  transform: translateY(-50%) perspective(1200px) rotateY(-2deg) scale(1.08);
  transform-origin: left center;
  -webkit-mask-image: radial-gradient(
    ellipse 78% 58% at 40% 43%,
    #000 0%,
    #000 56%,
    rgba(0, 0, 0, 0.74) 72%,
    transparent 100%
  );
  mask-image: radial-gradient(
    ellipse 78% 58% at 40% 43%,
    #000 0%,
    #000 56%,
    rgba(0, 0, 0, 0.74) 72%,
    transparent 100%
  );
}

.counter-content {
  position: relative;
  z-index: 4;
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
  padding: 42px 0 82px;
}

.counter-copy {
  position: relative;
  max-width: 650px;
  margin-left: auto;
  text-align: right;
  text-shadow: 0 2px 24px rgba(9, 8, 7, 0.9), 0 1px 2px rgba(9, 8, 7, 0.82);
}

.eyebrow {
  margin: 0 0 10px;
  color: #77e4c8;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

h1,
h2,
h3,
p {
  overflow-wrap: anywhere;
}

h1 {
  overflow-wrap: normal;
}

h1 {
  max-width: 760px;
  margin: 0;
  color: #fff8d8;
  font-size: clamp(3.2rem, 5.6vw, 4.45rem);
  line-height: 0.9;
  letter-spacing: 0;
}

h2 {
  margin: 0;
  color: #fff8d8;
  font-size: clamp(1.4rem, 2.4vw, 2.2rem);
  line-height: 1;
  letter-spacing: 0;
}

h3 {
  margin: 12px 0 8px;
  color: #fff8d8;
  font-size: 1.15rem;
  line-height: 1.1;
  letter-spacing: 0;
}

.lede {
  max-width: 580px;
  margin: 22px 0 26px auto;
  color: #dacdb5;
  font-size: clamp(1.08rem, 1.8vw, 1.35rem);
  line-height: 1.55;
}

.status-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.status-row span {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 11px;
  border: 1px solid rgba(255, 212, 95, 0.42);
  border-radius: 8px;
  background: rgba(32, 26, 22, 0.82);
  color: #ffe38a;
  font-size: 0.92rem;
  font-weight: 750;
}

.counter-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.counter-links a,
.mission-copy a,
.field-notes .section-heading a,
.note-grid a {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 11px;
  border: 1px solid rgba(119, 228, 200, 0.34);
  border-radius: 8px;
  background: rgba(13, 37, 34, 0.88);
  color: #d8fff7;
  font-size: 0.9rem;
  font-weight: 900;
  text-decoration: none;
}

.mission-strip,
.field-notes {
  position: relative;
  z-index: 3;
  margin-bottom: 20px;
  padding: 22px;
  border: 1px solid rgba(119, 228, 200, 0.26);
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(13, 37, 34, 0.88), rgba(32, 23, 19, 0.84)),
    rgba(18, 16, 14, 0.84);
}

.mission-strip {
  display: grid;
  grid-template-columns: minmax(280px, 0.92fr) minmax(0, 1.08fr);
  gap: 18px;
  align-items: stretch;
}

.mission-copy {
  display: grid;
  align-content: start;
  gap: 14px;
}

.mission-copy h2,
.mission-copy p {
  margin: 0;
}

.mission-copy p:not(.eyebrow),
.note-grid p {
  color: #dacdb5;
  line-height: 1.52;
}

.mission-copy a {
  width: fit-content;
  margin-top: 4px;
}

.principle-grid,
.note-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.principle-grid article,
.note-grid article {
  min-width: 0;
  padding: 16px;
  border: 1px solid rgba(255, 248, 216, 0.13);
  border-radius: 8px;
  background: rgba(20, 31, 29, 0.78);
}

.principle-grid span {
  width: 34px;
  height: 34px;
  display: inline-grid;
  place-items: center;
  border-radius: 8px;
  background: #eb3540;
  color: #fff8d8;
  font-size: 0.82rem;
  font-weight: 900;
}

.principle-grid h3,
.note-grid h3 {
  margin-top: 14px;
}

.principle-grid p,
.note-grid p {
  margin-bottom: 0;
}

.shop-grid {
  position: relative;
  z-index: 3;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
  gap: 20px;
  align-items: start;
}

.menu-panel,
.receipt-panel,
.receipt-wall,
.bot-kit {
  border: 1px solid rgba(255, 212, 95, 0.24);
  border-radius: 8px;
  background: rgba(18, 16, 14, 0.84);
}

.menu-panel {
  padding: 22px;
}

.section-heading,
.receipt-heading {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.menu-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.menu-item {
  min-height: 222px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
  padding: 16px;
  border: 1px solid rgba(255, 248, 216, 0.13);
  border-radius: 8px;
  background: rgba(32, 23, 19, 0.9);
}

.premium-item {
  grid-column: 1 / -1;
  min-height: 0;
  border-color: rgba(255, 212, 95, 0.48);
  background:
    linear-gradient(135deg, rgba(74, 20, 18, 0.94), rgba(17, 38, 35, 0.92)),
    rgba(32, 23, 19, 0.9);
}

.item-topline,
.totals div,
.cart-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.item-mark {
  width: 34px;
  height: 34px;
  display: inline-grid;
  place-items: center;
  border-radius: 8px;
  background: #eb3540;
  color: #fff8d8;
  font-size: 0.82rem;
  font-weight: 900;
}

.item-price {
  color: #ffd45f;
  font-size: 1.04rem;
  font-weight: 900;
}

.menu-item p,
.empty-cart p,
.cart-line span,
.totals span {
  color: #cabb9e;
  line-height: 1.42;
}

.value-list {
  display: grid;
  gap: 7px;
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
}

.value-list li {
  position: relative;
  padding-left: 18px;
  color: #f2dfb5;
  font-size: 0.92rem;
  line-height: 1.38;
}

.value-list li::before {
  position: absolute;
  top: 0.55em;
  left: 0;
  width: 7px;
  height: 7px;
  content: "";
  border-radius: 2px;
  background: #77e4c8;
}

.add-button,
.checkout-button,
.receipt-actions button,
.quantity-control button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  border-radius: 8px;
  cursor: pointer;
}

.add-button {
  width: 100%;
  background: #ffd45f;
  color: #15100c;
  font-weight: 900;
}

.receipt-panel {
  position: sticky;
  top: 18px;
  padding: 20px;
}

.receipt-heading {
  color: #ffd45f;
}

.cart-lines {
  display: grid;
  gap: 12px;
  margin: 18px 0;
}

.cart-line {
  min-height: 58px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 248, 216, 0.11);
}

.cart-line div:first-child {
  min-width: 0;
}

.cart-line strong {
  display: block;
  color: #fff8d8;
}

.cart-line span {
  display: block;
  font-size: 0.88rem;
}

.quantity-control {
  display: grid;
  grid-template-columns: 34px 28px 34px;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.quantity-control button {
  min-height: 34px;
  border: 1px solid rgba(255, 212, 95, 0.2);
  background: rgba(43, 33, 27, 0.9);
  color: #fff8d8;
}

.quantity-control span {
  text-align: center;
  color: #fff8d8;
  font-weight: 900;
}

.empty-cart {
  min-height: 148px;
  display: grid;
  place-items: center;
  gap: 10px;
  padding: 28px 14px;
  border: 1px dashed rgba(255, 212, 95, 0.32);
  border-radius: 8px;
  color: #ffd45f;
  text-align: center;
}

.totals {
  display: grid;
  gap: 8px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 248, 216, 0.13);
}

.totals strong {
  color: #fff8d8;
}

.grand-total {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 212, 95, 0.3);
}

.grand-total span,
.grand-total strong {
  color: #ffd45f;
  font-size: 1.1rem;
  font-weight: 900;
}

.checkout-button {
  width: 100%;
  margin-top: 18px;
  background: #eb3540;
  color: #fff8d8;
  font-weight: 900;
}

.alt-payment-panel {
  display: grid;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 248, 216, 0.13);
}

.alt-payment-panel .eyebrow {
  margin-bottom: 0;
}

.alt-payment-links {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.alt-payment-links a {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 10px;
  border: 1px solid rgba(255, 212, 95, 0.24);
  border-radius: 8px;
  background: rgba(43, 33, 27, 0.9);
  color: #fff8d8;
  font-size: 0.9rem;
  font-weight: 900;
  text-align: center;
  text-decoration: none;
}

.alt-payment-links a.disabled {
  cursor: not-allowed;
  opacity: 0.48;
}

.checkout-message {
  margin: 10px 0 0;
  color: #77e4c8;
  font-size: 0.92rem;
  font-weight: 800;
  line-height: 1.35;
}

.checkout-message.error {
  color: #ff8d72;
}

.embedded-checkout-panel {
  display: grid;
  gap: 12px;
  margin-top: 14px;
  padding: 12px;
  border: 1px solid rgba(119, 228, 200, 0.24);
  border-radius: 8px;
  background: rgba(11, 27, 25, 0.96);
}

.embedded-checkout-top {
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #77e4c8;
  font-size: 0.86rem;
  font-weight: 900;
  text-transform: uppercase;
}

.embedded-checkout-top button {
  width: 30px;
  height: 30px;
  display: inline-grid;
  place-items: center;
  border: 1px solid rgba(255, 248, 216, 0.14);
  border-radius: 8px;
  background: rgba(43, 33, 27, 0.9);
  color: #fff8d8;
  cursor: pointer;
}

.embedded-checkout-container {
  min-height: 470px;
  overflow: hidden;
  border-radius: 8px;
  background: #ffffff;
}

.checkout-button:disabled,
.receipt-actions button:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}

.oath-box {
  margin-top: 18px;
  padding: 16px;
  border-radius: 8px;
  background: rgba(13, 37, 34, 0.9);
  border: 1px solid rgba(119, 228, 200, 0.24);
}

blockquote {
  margin: 0;
  color: #e5fff8;
  font-size: 1rem;
  line-height: 1.5;
}

.receipt-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 12px;
}

.receipt-actions button {
  border: 1px solid rgba(255, 248, 216, 0.14);
  background: rgba(43, 33, 27, 0.9);
  color: #fff8d8;
  font-weight: 800;
}

.receipt-wall-shell {
  position: relative;
  z-index: 3;
  margin-top: 20px;
}

.receipt-wall {
  overflow: hidden;
}

.receipt-wall summary {
  min-height: 88px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  cursor: pointer;
  list-style: none;
}

.receipt-wall summary::-webkit-details-marker {
  display: none;
}

.receipt-wall summary::after {
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  content: "+";
  border: 1px solid rgba(255, 212, 95, 0.28);
  border-radius: 8px;
  background: rgba(43, 33, 27, 0.9);
  color: #ffd45f;
  font-size: 1.35rem;
  font-weight: 900;
  line-height: 1;
}

.receipt-wall[open] summary::after {
  content: "-";
}

.receipt-wall-count {
  margin-left: auto;
  color: #ffd45f;
  font-size: 0.92rem;
  font-weight: 900;
  white-space: nowrap;
}

.receipt-wall-body {
  display: grid;
  gap: 16px;
  padding: 0 22px 22px;
}

.receipt-wall-intro {
  max-width: 820px;
  margin: 0;
  color: #dacdb5;
  line-height: 1.5;
}

.receipt-ledger {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.receipt-ledger li {
  min-width: 0;
  display: grid;
  gap: 10px;
  padding: 16px;
  border: 1px solid rgba(255, 248, 216, 0.13);
  border-radius: 8px;
  background: rgba(32, 23, 19, 0.9);
}

.receipt-ledger-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.receipt-ledger strong {
  color: #fff8d8;
  line-height: 1.2;
}

.receipt-ledger code {
  flex: 0 0 auto;
  color: #77e4c8;
  font-size: 0.74rem;
  font-weight: 900;
}

.receipt-ledger p {
  margin: 0;
  color: #cabb9e;
  line-height: 1.42;
}

.receipt-ledger span {
  color: #ffd45f;
  font-size: 0.78rem;
  font-weight: 850;
}

.bot-receipts-json {
  max-height: 220px;
  margin: 0;
  padding: 14px;
  overflow: auto;
  border: 1px solid rgba(119, 228, 200, 0.18);
  border-radius: 8px;
  background: rgba(5, 12, 12, 0.72);
  color: #d8fff7;
  font-size: 0.78rem;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.bot-kit {
  position: relative;
  z-index: 3;
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 22px;
}

.bot-kit .section-heading {
  color: #ffd45f;
}

.kit-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) minmax(260px, 0.9fr);
  gap: 14px;
}

.kit-card {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border: 1px solid rgba(255, 248, 216, 0.13);
  border-radius: 8px;
  background: rgba(20, 31, 29, 0.86);
}

.kit-card-heading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.kit-card-heading h3 {
  margin: 0;
}

.kit-card pre {
  min-height: 210px;
  max-height: 310px;
  margin: 0;
  padding: 14px;
  overflow: auto;
  border: 1px solid rgba(119, 228, 200, 0.18);
  border-radius: 8px;
  background: rgba(5, 12, 12, 0.72);
  color: #d8fff7;
  font-size: 0.78rem;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.kit-card button,
.bot-file-links a {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  background: #77e4c8;
  color: #071211;
  font-weight: 900;
  text-decoration: none;
}

.kit-card button {
  width: 100%;
  cursor: pointer;
}

.bot-files p {
  margin: 0;
  color: #cabb9e;
  line-height: 1.48;
}

.bot-file-links {
  display: grid;
  gap: 10px;
  margin-top: auto;
}

.field-notes {
  margin-top: 0;
}

.note-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.note-date {
  margin: 0 0 4px;
  color: #ffd45f;
  font-size: 0.78rem;
  font-weight: 900;
}

.note-grid a {
  width: fit-content;
  margin-top: 14px;
}

@media (max-width: 940px) {
  .shop-grid {
    grid-template-columns: 1fr;
  }

  .mission-strip,
  .kit-grid {
    grid-template-columns: 1fr;
  }

  .principle-grid,
  .receipt-ledger {
    grid-template-columns: 1fr;
  }

  .counter {
    min-height: clamp(520px, 78vh, 680px);
    align-items: end;
  }

  .counter::before {
    background:
      linear-gradient(180deg, rgba(9, 8, 7, 0.08) 0%, rgba(9, 8, 7, 0.52) 46%, #090807 100%),
      linear-gradient(90deg, rgba(9, 8, 7, 0.18), rgba(9, 8, 7, 0.54));
  }

  .counter-bg {
    top: 0;
    left: -112px;
    width: min(136vw, 940px);
    height: 67%;
    object-position: left top;
    transform: perspective(1000px) rotateY(-2deg) scale(1.08);
  }

  .counter-content {
    padding: 250px 0 34px;
  }

  .receipt-panel {
    position: static;
  }
}

@media (max-width: 680px) {
  .app-shell {
    width: min(100% - 20px, 1180px);
  }

  .menu-list {
    grid-template-columns: 1fr;
  }

  h1 {
    font-size: clamp(2.2rem, 10vw, 2.9rem);
  }

  .counter {
    min-height: 680px;
  }

  .counter-bg {
    top: 0;
    left: -116px;
    width: 146vw;
    height: 60%;
    object-position: left top;
  }

  .counter-content {
    width: min(100% - 20px, 1180px);
    padding-top: 286px;
  }

  .menu-panel,
  .receipt-panel {
    padding: 16px;
  }

  .section-heading,
  .receipt-heading,
  .item-topline,
  .cart-line {
    align-items: flex-start;
  }

  .cart-line {
    flex-direction: column;
  }

  .alt-payment-links {
    grid-template-columns: 1fr;
  }

  .counter-links {
    justify-content: flex-start;
  }

  .note-grid {
    grid-template-columns: 1fr;
  }

  .receipt-wall summary {
    align-items: flex-start;
    padding: 18px 16px;
  }

  .receipt-wall-count {
    white-space: normal;
  }

  .receipt-wall-body {
    padding: 0 16px 16px;
  }

  .receipt-ledger-top {
    flex-direction: column;
  }
}
</style>
